<script setup lang="ts">
import { computed, toRef, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import type {
  BasePlanningBoard as BasePlanningBoardLogic,
  Planning,
} from "@/board/BasePlanningBoard";
import type {
  GroupIterations,
  PlanningBoardLocation,
} from "@/board/location/PlanningBoardLocation";
import StatusClassDistributionBar from "@/components/StatusDistribution/StatusClassDistributionBar.vue";
import {
  groupStatusDistribution,
  multiIterationStatusDistribution,
} from "@/components/StatusDistribution/statusDistribution";
import StickyNote from "@/components/StickyNote/StickyNote.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { loadState } from "@/components/utils/capacity";
import { useSafeDoubleClick } from "@/composables/useSafeDoubleClick";
import { relativeClientCoord } from "@/math/coordinate-utils";
import { divided } from "@/math/coordinates";
import type { ActionSource } from "@/model/action";
import type { InfoLevel } from "@/model/baseTypes";
import type { Board, GroupIteration } from "@/model/board";
import { normalLinkColorForBoard } from "@/model/colors";
import type { RelativeCoordinate } from "@/model/coordinates";
import type { Group, Iteration } from "@/model/session";
import { useBoardStore } from "@/store/board";
import { useClientSettingsStore } from "@/store/clientSettings";
import { usePanModeStore } from "@/store/panMode";
import { useSessionStore } from "@/store/session";
import { useTeamStore } from "@/store/team";
import { useWorkModeStore } from "@/store/workMode";
import { plusDays } from "@/utils/date";
import { toShortDateString } from "@/utils/dateFormat";

import BasePlanningBoardSection from "./BasePlanningBoardSection.vue";
import LinkLayers from "./internal/LinkLayer/LinkLayers.vue";
import { useFluidBoard } from "./internal/useFluidBoard";
import { usePlanningLegend } from "./internal/usePlanningLegend";

const props = defineProps<{ board: Board }>();

const rootRef = useTemplateRef("root");

const { t } = useI18n();

const { active, isLoading, levelOfDetails, focusAndBlur, fixedFontSize, boardSize } = useFluidBoard(
  toRef(props, "board"),
  rootRef,
);

defineExpose(focusAndBlur);

const { showLegend } = usePlanningLegend(props.board, active);

const groups = computed(() => useBoardStore().baseBoard.groups);
const nonEmptyGroups = computed(() => groups.value.filter((group) => group.name));
const iterationNow = computed(() => useSessionStore().iterationProgress(new Date()));
const iterations = computed(() => useSessionStore().iterations);
const isExecutionMode = computed(() => useWorkModeStore().isExecutionMode);
const baseBoard = computed(() => useBoardStore().baseBoard as BasePlanningBoardLogic<Planning>);
const fieldSize = computed(() =>
  divided(boardSize.value, useSessionStore().iterations.length + 1, groups.value.length + 0.5),
);

function fieldClass(group: GroupIterations, iter?: GroupIteration) {
  const groupColor = group.current ? "group-current" : "";
  if (!iter || !useClientSettingsStore().overload) {
    return groupColor;
  }

  const state = loadState(iter.load!, iter.velocity!);

  const customStates: Record<InfoLevel, string> = {
    default: groupColor,
    ok: groupColor,
    warn: "warn",
    error: "overload",
  };

  return customStates[state];
}

function getTeamNameById(id: string) {
  return useTeamStore().findTeam({ id, withExternal: true })?.name;
}

function location(c: RelativeCoordinate | number, top?: number) {
  return useBoardStore().boardLocation(c, top) as PlanningBoardLocation;
}

function zoomToRegionGlobal(e: MouseEvent) {
  const loc = location(relativeClientCoord(e));
  baseBoard.value.zoomAction(loc)?.("mouse");
}

const doubleClickZoom = useSafeDoubleClick(zoomToRegionGlobal);

function overview(
  loc: PlanningBoardLocation,
  source: ActionSource = "mouse",
  setReturnFocus?: string, // Selector of element to be focused when overview is closed
) {
  baseBoard.value.overviewAction(loc, setReturnFocus)?.(source);
}

function groupKey(prefix: string, group: Group, index: number) {
  return "group-" + prefix + (group.id ? "-id-" + group.id : "-index-" + index);
}

function dates(iter: Iteration) {
  return t("date.range", {
    from: toShortDateString(iter.start),
    to: toShortDateString(plusDays(iter.end, -1)),
  });
}
</script>

<template>
  <div :id="board.id" ref="root" class="planning-board board">
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div class="backdrop" v-on="doubleClickZoom">
      <LoadingIndicator v-if="active && isLoading" global />
      <LinkLayers :board :color="normalLinkColorForBoard(board.type)" />

      <!-- eslint-disable-next-line vuejs-accessibility/no-redundant-roles -->
      <table role="table" class="backdrop" :class="{ 'no-events': usePanModeStore().active }">
        <!-- Column headers -->
        <thead>
          <tr class="iteration-header-row">
            <!-- Column 'In planning' -->
            <th
              scope="col"
              class="column clickable"
              :style="{ width: fieldSize.x / 2 + 'px', height: fieldSize.y / 2 + 'px' }"
            >
              <BasePlanningBoardSection
                :aria-label="$t('programBoard.inPlanning')"
                :location="location(-2, -1)"
                :board
                class="aria-wrapper"
                @overview="overview"
              >
                <div class="top-text in-planning">
                  <div class="fit-font">
                    <div class="title-col h3">
                      {{ $t("programBoard.inPlanning") }}
                    </div>
                  </div>
                </div>
              </BasePlanningBoardSection>
            </th>

            <!-- Empty column header (above the row headers) -->
            <th
              scope="col"
              style="position: absolute"
              :style="{
                left: fieldSize.x / 2 + 'px',
                width: fieldSize.x / 2 + 'px',
                height: fieldSize.y / 2 + 'px',
              }"
            >
              <BasePlanningBoardSection
                :aria-label="$t('label.planningBoard.boardSection')"
                :location="location(-1, -1)"
                :board
                class="aria-wrapper"
                @overview="overview"
              />
            </th>
            <!-- Iteration column headers -->
            <th
              v-for="(iter, iterIndex) in iterations"
              :key="`row-${iterIndex}`"
              scope="col"
              style="position: absolute"
              :style="{
                left: fieldSize.x * (iterIndex + 1) + 'px',
                width: fieldSize.x + 'px',
                height: fieldSize.y / 2 + 'px',
              }"
            >
              <BasePlanningBoardSection
                :aria-label="iter.name"
                :location="location(iterations[iterIndex].id, -1)"
                :board
                class="aria-wrapper"
                @overview="overview"
              >
                <div class="top-text">
                  <div class="fit-font">
                    <div class="h3">{{ iter.name }}</div>
                    <div class="h4">{{ dates(iter) }}</div>
                  </div>
                  <StatusClassDistributionBar
                    v-if="isExecutionMode"
                    :iteration="iter"
                    :data-testid="`iteration-status-distribution-${iterIndex}`"
                    class="iteration-distribution"
                    :value="multiIterationStatusDistribution(groups, iterIndex)"
                    :filters="{ boardId: board.id, iterationId: iter.id }"
                    source-item-type="iteration"
                    :title="iter.name"
                  />
                </div>
              </BasePlanningBoardSection>
            </th>
          </tr>
        </thead>

        <!-- Rows -->
        <tbody>
          <tr
            v-for="(group, groupIndex) in groups"
            :key="groupKey('name', group, groupIndex)"
            class="field"
            :class="[fieldClass(group)]"
            :style="{
              top: fieldSize.y * (groupIndex + 0.5) + 'px',
              left: fieldSize.x * 0.5 + 'px',
              height: fieldSize.y + 'px',
              width: fieldSize.x / 2 + 'px',
            }"
          >
            <!--
Section under 'In Planning' (need 1 per row, because the cards are still sorted per group
and VO doesn't recognize cells that span multiple rows)
-->
            <td
              :style="{
                position: 'absolute',
                left: -fieldSize.x / 2 + 'px',
                width: fieldSize.x / 2 + 'px',
                height: ' 100%',
              }"
            >
              <BasePlanningBoardSection
                :aria-label="$t('label.planningBoard.boardSection')"
                :location="location(-2, groupIndex)"
                :board
                class="aria-wrapper"
                @overview="overview"
              />
            </td>
            <!-- Row headers (group names) -->
            <th scope="row" class="group-field">
              <BasePlanningBoardSection
                :aria-label="group.name || $t('label.planningBoard.extraGroup')"
                :location="location(-1, groupIndex)"
                :board
                class="aria-wrapper"
                @overview="overview"
              >
                <div class="fit-font">
                  <div class="group-name" :class="{ 'current-group': group.current }">
                    <!-- show external orgs in a tooltip for extra swimlanes -->
                    <div v-if="group.externalOrgIds?.length">
                      <BaseTooltip position="bottom">
                        <span class="group-name-text">{{ group.name }}</span>
                        <template #content>
                          <div
                            v-for="id in group.externalOrgIds"
                            :key="id"
                            class="extra-swimlane-tooltip"
                          >
                            <SvgIcon name="persona/external-team" width="14" height="14" />
                            {{ getTeamNameById(id) }}
                          </div>
                        </template>
                      </BaseTooltip>
                    </div>
                    <BaseTooltip
                      v-else
                      position="bottom"
                      only-show-on-overflow
                      :content="group.name"
                    >
                      <span class="group-name-text">{{ group.name }}</span>
                    </BaseTooltip>
                  </div>
                </div>
                <StatusClassDistributionBar
                  v-if="group.id && isExecutionMode"
                  :data-testid="`group-status-distribution-${group.id}`"
                  class="iteration-distribution"
                  :value="groupStatusDistribution(group)"
                  :filters="
                    board?.type === 'solution'
                      ? { boardId: board.id, artId: Number(group.id) }
                      : { boardId: board.id, teamId: group.id }
                  "
                  :source-item-type="board?.type === 'solution' ? 'art' : 'team'"
                  :title="group.name"
                />
              </BasePlanningBoardSection>
            </th>
            <!-- Cells in the row, one per iteration -->
            <td
              v-for="(iter, iterIndex) in group.iterations"
              :key="iterIndex"
              class="field"
              :class="[fieldClass(group, iter)]"
              :style="{
                top: 0,
                left: fieldSize.x * (iterIndex + 0.5) + 'px',
                height: fieldSize.y + 'px',
                width: fieldSize.x + 'px',
              }"
            >
              <BasePlanningBoardSection
                :aria-label="$t('label.planningBoard.boardSection')"
                :location="location(iterations[iterIndex].id, groupIndex)"
                :board
                class="aria-wrapper"
                @overview="overview"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Backdrop (presentation, not content) -->
      <div class="backdrop" :style="{ fontSize: fixedFontSize + '%' }" style="pointer-events: none">
        <div
          v-if="isExecutionMode"
          class="time-line"
          :style="{
            width: iterationNow.iterationsPassed * fieldSize.x + 'px',
            left: fieldSize.x + 'px',
            top: fieldSize.y * 0.5 + 'px',
          }"
        >
          <svg>
            <line x1="0" y1="0" x2="0" y2="100%" />
          </svg>
        </div>
        <div
          v-for="(group, groupIndex) in nonEmptyGroups"
          :key="groupKey('iter', group, groupIndex)"
          class="field horizontal-line"
          :style="{
            lineHeight: fieldSize.y + 'px',
            top: fieldSize.y * (groupIndex + 0.5) + 'px',
            left: fieldSize.x * 0.5 + 'px',
            height: fieldSize.y + 'px',
          }"
        >
          <template v-if="showLegend">
            <div
              v-for="iterIndex in iterations.length"
              :key="iterIndex"
              class="legend group-legend"
              :style="{
                left: (iterIndex - 0.5) * fieldSize.x - fieldSize.y / 2 + 'px',
                width: fieldSize.y + 'px',
              }"
            >
              <div>{{ group.name }}</div>
            </div>
          </template>
        </div>
        <div
          v-for="(iter, iterIndex) in iterations"
          :key="iterIndex"
          class="column"
          :style="{ left: fieldSize.x * (iterIndex + 1) + 'px', width: fieldSize.x + 'px' }"
        >
          <template v-if="showLegend">
            <div
              v-for="groupIndex in groups.length"
              :key="groupIndex"
              class="legend iter-legend"
              :style="{ top: (groupIndex - 0.5) * fieldSize.y + 'px' }"
            >
              <div>{{ iter.name }}</div>
            </div>
          </template>
        </div>
      </div>

      <!-- Iteration columns -->
      <div
        v-for="(_, iterIndex) in iterations"
        :key="`row1-${iterIndex}`"
        class="column"
        :style="{ left: fieldSize.x * (iterIndex + 1) + 'px', width: fieldSize.x + 'px' }"
      />
    </div>

    <!-- Cards -->
    <StickyNote
      v-for="card in board.cards"
      :key="card.data.id"
      :card="card.data"
      :card-meta="card.meta"
      :level-of-details
    />
  </div>
</template>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/board";
@use "@/styles/variables/colors";
@use "@/styles/time-line" as *;
@use "@/styles/z-index";
@use "@/styles/mixins/a11y";
@use "@/styles/mixins/utils";

$header-size: board.len(14px);

.no-events {
  pointer-events: none;
}

.extra-swimlane-tooltip {
  display: flex;
  align-items: center;
  gap: 5px;
}

.planning-board {
  @include a11y.board;

  .group-name {
    @include board.board-control;

    position: relative;
    font-weight: font.$weight-medium;
    font-size: $header-size;
    margin: 0;
    width: 100%;
    overflow: hidden;

    &.current-group {
      font-weight: font.$weight-extra-bold;
      background-color: colors.$background-grey-light;
    }

    .base-tooltip {
      @include utils.ellipsis;

      display: block;
    }
  }

  .aria-wrapper {
    @include a11y.board-section;

    position: relative;
    height: 100%;
    width: 100%;

    .inner-wrapper {
      padding: 8% 8% 0;

      .wrapper {
        display: flex;
        flex-direction: column;
      }
    }
  }

  .field,
  .group-field {
    .grid-cell {
      @include a11y.board-section;

      position: relative;
      height: 100%;
      width: 100%;
    }
  }

  .field {
    &.overload {
      background-color: colors.$alternative-red-background-light;
    }

    &.warn {
      background-color: colors.$alternative-brown-background-light;
    }
  }

  .column {
    position: absolute;
    height: 100%;

    &:not(.clickable) {
      pointer-events: none;
    }
  }

  .horizontal-line {
    width: 100%;
    height: 100%;
  }

  .time-line {
    margin-top: board.len(-3px);
    margin-left: board.len(3px);
  }

  .top-text {
    position: absolute;
    white-space: nowrap;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    inset: 8% calc(6% / 2);

    & > div.fit-font {
      display: flex;
      overflow: hidden;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      gap: board.len(14px);

      .h3 {
        @include board.board-control;

        font-weight: font.$weight-bold;
        font-size: $header-size;
      }

      .h4 {
        @include board.board-control;

        font-weight: font.$weight-normal;
        color: colors.$text-subtle;
      }
    }
  }

  .group-current {
    background-color: colors.$background-grey-light;
  }

  .group-field {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: absolute;

    .base-planning-board-section,
    .base-planning-board-section > .wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .fit-font {
      min-width: 0;

      &.min-size {
        white-space: normal;
        overflow-wrap: break-word;
      }
    }

    .iteration-distribution {
      margin-top: board.len(2px);
      background-color: inherit;
    }
  }

  .in-planning {
    justify-content: flex-start;
    align-items: flex-start;
    inset: 8% 6%;

    .title-col.h3 {
      @include board.board-control;

      font-size: $header-size;
    }
  }

  .legend {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    text-align: center;
    z-index: z-index.$board;

    div {
      @include utils.ellipsis;

      display: inline-block;
      height: board.len(60px);
      line-height: board.len(60px);
      padding: 0 board.len(15px);
      border-radius: board.len(15px);
      max-width: 80%;
      background-color: colors.$background-grey;
    }
  }

  .iter-legend {
    transform: translateY(-50%);
    width: 100%;
  }

  .group-legend {
    transform: rotate(-90deg);
    height: 100%;
  }

  .iteration-distribution {
    @include board.board-control;

    --distribution-border-width: #{board.len(1px)};

    height: board.len(10px);
    width: 100%;
  }
}
</style>
